import type { MediaChunkAccepted, MediaInitResponse, MediaItem } from '@bssb/contracts';
import { v7 as uuidv7 } from 'uuid';
import { ApiError, apiRequest } from './api-client.ts';
import { db, META_KEYS, type MediaQueueItem, readMeta } from './db.ts';

/**
 * Antrean media perangkat (T-P5-15, FR-VISIT-05/06, FR-SYNC-07).
 *
 * Foto dikecilkan dan diberi watermark di kanvas sebelum disimpan sebagai Blob:
 * kuota IndexedDB perangkat lapangan terbatas, dan foto 4 MB langsung dari
 * kamera akan menghabiskannya dalam belasan kunjungan. Watermark kanvas ini
 * untuk pembacaan manusia — bukti yang otoritatif tetap watermark server.
 */

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.75;
const DEFAULT_CHUNK_BYTES = 1024 * 1024;
const MAX_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 15 * 60 * 1000;

export interface WatermarkInfo {
  capturedAt: Date;
  latitude: number | null;
  longitude: number | null;
  collectorName: string;
  loanNumber: string;
}

/** Kecilkan, beri watermark, lalu kembalikan JPEG siap antre. */
export async function preparePhoto(file: Blob, info: WatermarkInfo): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Kanvas tidak tersedia untuk memproses foto');

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  drawWatermark(context, width, height, [
    `${info.capturedAt.toLocaleString('id-ID')}`,
    info.latitude !== null && info.longitude !== null
      ? `${info.latitude.toFixed(5)}, ${info.longitude.toFixed(5)}`
      : 'lokasi tidak tersedia',
    `${info.collectorName} · ${info.loanNumber}`,
  ]);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('Foto gagal dikompresi');

  return blob;
}

function drawWatermark(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  lines: string[],
): void {
  const fontSize = Math.max(12, Math.round(width / 45));
  const lineHeight = Math.round(fontSize * 1.35);
  const boxHeight = lineHeight * lines.length + fontSize;

  context.fillStyle = 'rgba(0, 0, 0, 0.55)';
  context.fillRect(0, height - boxHeight, width, boxHeight);
  context.fillStyle = '#ffffff';
  context.font = `${fontSize}px sans-serif`;

  lines.forEach((line, index) => {
    context.fillText(line, fontSize / 2, height - boxHeight + lineHeight * (index + 1));
  });
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface EnqueueMediaInput {
  blob: Blob;
  kind: MediaQueueItem['kind'];
  ownerClientUuid: string | null;
  capturedAt?: Date;
  latitude?: number | null;
  longitude?: number | null;
}

/** Simpan blob ke antrean; unggahannya menyusul saat sinkronisasi berjalan. */
export async function enqueueMedia(input: EnqueueMediaInput): Promise<string> {
  const clientUuid = uuidv7();
  const now = new Date().toISOString();

  await db.mediaQueue.put({
    clientUuid,
    ownerClientUuid: input.ownerClientUuid,
    kind: input.kind,
    mimeType: input.blob.type || 'image/jpeg',
    blob: input.blob,
    sizeBytes: input.blob.size,
    sha256: await sha256Hex(input.blob),
    capturedAt: (input.capturedAt ?? new Date()).toISOString(),
    latitude: input.latitude ?? null,
    longitude: input.longitude ?? null,
    status: 'PENDING',
    mediaId: null,
    uploadedBytes: 0,
    attempts: 0,
    nextAttemptAt: now,
    createdAt: now,
    lastError: null,
  });

  return clientUuid;
}

export interface MediaFlushReport {
  uploaded: number;
  failed: number;
  skipped: number;
}

/**
 * Unggah seluruh antrean yang sudah jatuh tempo.
 *
 * Dipanggil `syncEngine` **setelah** outbox terkirim: kunjungan yang sudah ada
 * di server dapat langsung menautkan medianya, sedangkan urutan sebaliknya
 * membuat penautan tertunda satu putaran.
 */
export async function flushMediaQueue(): Promise<MediaFlushReport> {
  const report: MediaFlushReport = { uploaded: 0, failed: 0, skipped: 0 };
  const now = Date.now();

  const due = (await db.mediaQueue.where('status').anyOf('PENDING', 'UPLOADING').toArray()).filter(
    (item) => Date.parse(item.nextAttemptAt) <= now,
  );

  for (const item of due) {
    try {
      await uploadItem(item);
      report.uploaded += 1;
    } catch (error) {
      report.failed += 1;
      await recordFailure(item, error);
    }
  }

  report.skipped = due.length - report.uploaded - report.failed;
  return report;
}

async function uploadItem(item: MediaQueueItem): Promise<void> {
  const limits = await readMeta<{ mediaChunkBytes?: number }>(META_KEYS.limits);
  const chunkBytes = limits?.mediaChunkBytes ?? DEFAULT_CHUNK_BYTES;

  const init = await apiRequest<MediaInitResponse>('/media/init', {
    method: 'POST',
    body: {
      items: [
        {
          clientUuid: item.clientUuid,
          kind: item.kind,
          mimeType: item.mimeType,
          sizeBytes: item.sizeBytes,
          sha256: item.sha256,
          capturedAt: item.capturedAt,
          latitude: item.latitude,
          longitude: item.longitude,
          ownerClientUuid: item.ownerClientUuid,
        },
      ],
    },
  });

  const registered = init.items[0];
  if (!registered) throw new Error('Server tidak mengembalikan pendaftaran media');

  await db.mediaQueue.put({
    ...item,
    status: 'UPLOADING',
    mediaId: registered.mediaId,
    uploadedBytes: registered.receivedBytes,
  });

  // Titik lanjut datang dari server, bukan dari catatan lokal: perangkat yang
  // mati di tengah unggahan tidak tahu potongan mana yang benar-benar sampai.
  let offset = registered.receivedBytes;

  while (offset < item.sizeBytes) {
    const end = Math.min(offset + chunkBytes, item.sizeBytes);
    const chunk = item.blob.slice(offset, end);

    const accepted = await apiRequest<MediaChunkAccepted>(`/media/${registered.mediaId}/content`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/octet-stream',
        'content-range': `bytes ${offset}-${end - 1}/${item.sizeBytes}`,
      },
      raw: chunk,
    });

    offset = accepted.receivedBytes;
    await db.mediaQueue.update(item.clientUuid, { uploadedBytes: offset });
  }

  const completed = await apiRequest<MediaItem>(`/media/${registered.mediaId}/complete`, {
    method: 'POST',
    body: { sha256: item.sha256 },
  });

  // Blob dibuang setelah server mengonfirmasi checksum — itu satu-satunya bukti
  // bahwa berkasnya benar-benar utuh di sana.
  await db.mediaQueue.put({
    ...item,
    status: 'UPLOADED',
    mediaId: completed.id,
    uploadedBytes: item.sizeBytes,
    blob: new Blob([], { type: item.mimeType }),
    lastError: null,
  });
}

async function recordFailure(item: MediaQueueItem, error: unknown): Promise<void> {
  const message = error instanceof Error ? error.message : String(error);
  const permanent = error instanceof ApiError && error.isPermanent;
  const attempts = item.attempts + 1;
  const delay = Math.min(2 ** attempts * BASE_BACKOFF_MS, MAX_BACKOFF_MS);

  await db.mediaQueue.put({
    ...item,
    attempts,
    lastError: message,
    status: permanent || attempts >= MAX_ATTEMPTS ? 'FAILED' : 'PENDING',
    nextAttemptAt: new Date(Date.now() + delay).toISOString(),
  });
}

/** Bersihkan antrean yang sudah terkirim; dipanggil setelah putaran sinkronisasi. */
export async function pruneUploadedMedia(): Promise<number> {
  const uploaded = await db.mediaQueue.where('status').equals('UPLOADED').primaryKeys();
  await db.mediaQueue.bulkDelete(uploaded as string[]);
  return uploaded.length;
}

/** Dipakai layar antrean: ringkasan yang cukup untuk indikator header. */
export async function mediaQueueSummary(): Promise<{
  pending: number;
  failed: number;
  bytes: number;
}> {
  const items = await db.mediaQueue.toArray();

  return {
    pending: items.filter((item) => item.status !== 'UPLOADED' && item.status !== 'FAILED').length,
    failed: items.filter((item) => item.status === 'FAILED').length,
    bytes: items.reduce((total, item) => total + item.sizeBytes, 0),
  };
}
